import { signOut } from 'firebase/auth'
import { auth } from '@/firebase/config'
import {
  AUTH_LOCAL_STORAGE_KEYS,
  AUTH_LOCAL_STORAGE_PREFIXES,
  AUTH_SESSION_MAX_AGE_MS,
  AUTH_SESSION_STARTED_KEY,
  AUTH_SESSION_STORAGE_KEYS
} from '@/constants/authSession'

function safeRemove(storage, key) {
  try {
    storage?.removeItem(key)
  } catch {
    // Storage may be unavailable (private mode / quota).
  }
}

function removeByPrefixes(storage, prefixes) {
  try {
    if (!storage) return
    const keys = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key) keys.push(key)
    }
    keys.forEach((key) => {
      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        storage.removeItem(key)
      }
    })
  } catch {
    // ignore
  }
}

export function markAuthSessionStart() {
  try {
    localStorage.setItem(AUTH_SESSION_STARTED_KEY, String(Date.now()))
  } catch {
    // ignore
  }
}

export function clearAuthRelatedStorage() {
  if (typeof window === 'undefined') return

  AUTH_LOCAL_STORAGE_KEYS.forEach((key) => safeRemove(localStorage, key))
  AUTH_SESSION_STORAGE_KEYS.forEach((key) => safeRemove(sessionStorage, key))
  removeByPrefixes(localStorage, AUTH_LOCAL_STORAGE_PREFIXES)
}

function getStoredSessionStartedAt() {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_STARTED_KEY)
    if (!raw) return null
    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  } catch {
    return null
  }
}

function getLastSignInMs(user) {
  const lastSignInTime = user?.metadata?.lastSignInTime
  if (!lastSignInTime) return null
  const ms = new Date(lastSignInTime).getTime()
  return Number.isFinite(ms) ? ms : null
}

export function isAuthSessionExpired(user) {
  if (!user) return false

  const now = Date.now()
  const lastSignInMs = getLastSignInMs(user)
  if (lastSignInMs != null && now - lastSignInMs > AUTH_SESSION_MAX_AGE_MS) {
    return true
  }

  const startedAt = getStoredSessionStartedAt()
  if (startedAt != null && now - startedAt > AUTH_SESSION_MAX_AGE_MS) {
    return true
  }

  // No reliable timestamp available — do not lock the user out; stamp is set on enforce.
  return false
}

export async function enforceAuthSessionExpiry(user) {
  if (!user) return false

  if (!isAuthSessionExpired(user)) {
    if (getStoredSessionStartedAt() == null) {
      const lastSignInMs = getLastSignInMs(user)
      try {
        localStorage.setItem(
          AUTH_SESSION_STARTED_KEY,
          String(lastSignInMs ?? Date.now())
        )
      } catch {
        // ignore
      }
    }
    return false
  }

  clearAuthRelatedStorage()

  try {
    await signOut(auth)
  } catch (error) {
    console.warn('[authSessionExpiry] Forced sign-out failed:', error)
  }

  return true
}
